/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Calendar, Clock, X, Stethoscope, History, CreditCard } from 'lucide-react';
import { Link } from 'react-router-dom';
import logger from '@/lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  pending: { label: 'In Attesa', className: 'bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))]', color: '#F59E0B' },
  confirmed: { label: 'Confermato', className: 'bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))]', color: '#1E7A58' },
  rejected: { label: 'Rifiutato', className: 'bg-[hsl(var(--destructive))] text-[hsl(var(--destructive-foreground))]', color: '#E5484D' },
  cancelled: { label: 'Annullato', className: 'bg-slate-500 text-white', color: '#64748B' },
  completed: { label: 'Completato', className: 'bg-[hsl(var(--info))] text-[hsl(var(--info-foreground))]', color: '#1677C7' },
};

const ROLE_LABELS = { patient: 'Paziente', operator: 'Operatore', admin: 'Admin', guest: 'Ospite' };

const PAYMENT_MAP = {
  paid: { label: 'Pagato', className: 'bg-emerald-100 text-emerald-800' },
  pending: { label: 'Da pagare', className: 'bg-amber-100 text-amber-800' },
  expired: { label: 'Da pagare', className: 'bg-amber-100 text-amber-800' },
  failed: { label: 'Pagamento fallito', className: 'bg-red-100 text-red-700' },
};

export default function MieiAppuntamenti() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyAppt, setHistoryAppt] = useState(null);
  const [payingId, setPayingId] = useState(null);

  const fetchAppts = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/appointments/my`)
      .then(r => setAppointments(r.data))
      .catch(err => logger.error('Fetch appointments error:', err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAppts(); }, [fetchAppts]);

  const handleCancel = async (id) => {
    try {
      await axios.put(`${API}/appointments/${id}/cancel`, {});
      toast.success('Appuntamento annullato');
      fetchAppts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const handlePayNow = async (appt) => {
    setPayingId(appt.id);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        appointment_id: appt.id,
        origin_url: window.location.origin
      }, {});
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'avvio del pagamento');
      setPayingId(null);
    }
  };

  const upcoming = appointments.filter(a => ['pending', 'confirmed'].includes(a.status));
  const past = appointments.filter(a => ['completed', 'cancelled', 'rejected'].includes(a.status));

  const renderTimeline = (appt) => {
    const history = appt.status_history || [];
    if (history.length === 0) return <p className="text-sm text-muted-foreground">Nessuno storico disponibile</p>;
    return (
      <div className="space-y-3">
        {history.map((h, i) => (
          <div key={`${h.new_status}-${h.timestamp}-${i}`} className="flex gap-3">
            <div className="flex flex-col items-center">
              <div className="w-3 h-3 rounded-full" style={{backgroundColor: STATUS_MAP[h.new_status]?.color || '#94a3b8'}} />
              {i < history.length - 1 && <div className="w-px flex-1 bg-[hsl(var(--border))] mt-1" />}
            </div>
            <div className="flex-1 pb-3">
              <div className="flex items-center gap-2">
                <Badge className={STATUS_MAP[h.new_status]?.className}>{STATUS_MAP[h.new_status]?.label || h.new_status}</Badge>
                {h.actor_role && <span className="text-xs text-muted-foreground">da {ROLE_LABELS[h.actor_role] || h.actor_role}</span>}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {h.timestamp ? new Date(h.timestamp).toLocaleString('it-IT', {dateStyle:'medium', timeStyle:'short'}) : ''}
              </p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="py-10 sm:py-14">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#1F2A37]" style={{fontFamily:'Space Grotesk'}}>I Miei Appuntamenti</h1>
            <p className="text-muted-foreground">Gestisci i tuoi appuntamenti</p>
          </div>
          <Link to="/prenota">
            <Button className="bg-[#238A92] hover:bg-[#0F5E66] text-white">
              <Calendar size={14} className="mr-1" /> Nuovo
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
        ) : appointments.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Calendar size={48} className="mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium text-[#1F2A37] mb-2" style={{fontFamily:'Space Grotesk'}}>Nessun appuntamento</p>
              <p className="text-muted-foreground mb-4">Non hai ancora prenotato nessun appuntamento</p>
              <Link to="/prenota"><Button className="bg-[#238A92] hover:bg-[#0F5E66] text-white">Prenota Ora</Button></Link>
            </CardContent>
          </Card>
        ) : (
          <>
            {upcoming.length > 0 && (
              <div className="mb-8">
                <h2 className="text-lg font-semibold text-[#1F2A37] mb-4" style={{fontFamily:'Space Grotesk'}}>Prossimi Appuntamenti</h2>
                <div className="space-y-3">
                  {upcoming.map(a => (
                    <Card key={a.id} className="hover:shadow-[var(--shadow-md)] transition-shadow duration-200">
                      <CardContent className="p-4 sm:p-6">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-[#1F2A37]" style={{fontFamily:'Space Grotesk'}}>{a.service_nome}</h3>
                              <Badge className={STATUS_MAP[a.status]?.className}>{STATUS_MAP[a.status]?.label}</Badge>
                              {PAYMENT_MAP[a.payment_status] && (
                                <Badge className={PAYMENT_MAP[a.payment_status].className} data-testid="payment-status-badge">
                                  {PAYMENT_MAP[a.payment_status].label}{a.payment_status !== 'paid' && a.payment_amount ? ` ${Number(a.payment_amount).toFixed(2)} EUR` : ''}
                                </Badge>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1"><Stethoscope size={14} /> Dott. {a.doctor_nome} {a.doctor_cognome}</span>
                              <span className="flex items-center gap-1"><Calendar size={14} /> {a.data}</span>
                              <span className="flex items-center gap-1"><Clock size={14} /> {a.ora} ({a.durata_minuti} min)</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {['pending', 'expired', 'failed'].includes(a.payment_status) && Number(a.payment_amount) > 0 && (
                              <Button
                                size="sm"
                                onClick={() => handlePayNow(a)}
                                disabled={payingId === a.id}
                                className="bg-[#1E7A58] hover:bg-[#155C42] text-white"
                                data-testid="pay-now-list-button"
                              >
                                <CreditCard size={14} className="mr-1" /> {payingId === a.id ? 'Avvio...' : 'Paga ora'}
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => setHistoryAppt(a)} title="Storico">
                              <History size={14} className="mr-1" /> Storico
                            </Button>
                            {(a.status === 'pending' || a.status === 'confirmed') && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="patient-appointments-cancel-button">
                                    <X size={14} className="mr-1" /> Annulla
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Conferma Annullamento</AlertDialogTitle>
                                    <AlertDialogDescription>Sei sicuro di voler annullare questo appuntamento?</AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>No, mantieni</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleCancel(a.id)} className="bg-red-600 hover:bg-red-700">Si, annulla</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {past.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-[#1F2A37] mb-4" style={{fontFamily:'Space Grotesk'}}>Storico</h2>
                <div className="space-y-3">
                  {past.map(a => (
                    <Card key={a.id} className="opacity-70">
                      <CardContent className="p-4 sm:p-6">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-[#1F2A37]" style={{fontFamily:'Space Grotesk'}}>{a.service_nome}</h3>
                              <Badge className={STATUS_MAP[a.status]?.className}>{STATUS_MAP[a.status]?.label}</Badge>
                            </div>
                            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                              <span>Dott. {a.doctor_nome} {a.doctor_cognome}</span>
                              <span>{a.data} alle {a.ora}</span>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => setHistoryAppt(a)}>
                            <History size={14} className="mr-1" /> Storico
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Status Timeline Dialog */}
        <Dialog open={!!historyAppt} onOpenChange={() => setHistoryAppt(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{fontFamily:'Space Grotesk'}}>Storico Appuntamento</DialogTitle>
            </DialogHeader>
            {historyAppt && (
              <div className="space-y-4">
                <div className="bg-[hsl(var(--secondary))] rounded-lg p-4">
                  <p className="font-medium text-sm">{historyAppt.service_nome}</p>
                  <p className="text-xs text-muted-foreground">Dott. {historyAppt.doctor_nome} {historyAppt.doctor_cognome} - {historyAppt.data} alle {historyAppt.ora}</p>
                </div>
                <div>
                  <p className="text-sm font-medium mb-3" style={{fontFamily:'Space Grotesk'}}>Timeline Stato</p>
                  {renderTimeline(historyAppt)}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
